// Global store for API requests that persists across component mounts/unmounts
interface ApiRequest {
  id: string;
  method: string;
  url: string;
  status?: number;
  statusText?: string;
  responseTime?: number;
  timestamp: number;
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  requestPayload?: any;
  responseData?: any;
  error?: string;
  type: 'fetch' | 'xhr';
}

class ApiRequestStore {
  private requests: ApiRequest[] = [];
  private listeners: Set<(requests: ApiRequest[]) => void> = new Set();
  private isIntercepted = false;
  private originalFetch: typeof fetch | null = null;
  private originalXHROpen: any = null;
  private originalXHRSend: any = null;
  private originalAxiosCreate: any = null;
  private axiosModule: any = null;
  private maxRequests = 100;

  subscribe(listener: (requests: ApiRequest[]) => void) {
    this.listeners.add(listener);
    // Immediately notify with current state
    listener([...this.requests]);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach(listener => listener([...this.requests]));
  }

  addRequest(request: ApiRequest) {
    this.requests = [request, ...this.requests].slice(0, this.maxRequests);
    this.notify();
  }

  updateRequest(id: string, updates: Partial<ApiRequest>) {
    const index = this.requests.findIndex(r => r.id === id);
    if (index >= 0) {
      this.requests[index] = { ...this.requests[index], ...updates };
      this.notify();
    }
  }

  clearRequests() {
    this.requests = [];
    this.notify();
  }

  getRequests(): ApiRequest[] {
    return [...this.requests];
  }

  setupInterception() {
    if (this.isIntercepted) return;
    
    const isEnabled = process.env.NODE_ENV !== 'production' || localStorage.getItem('ENABLE_DEBUG') === 'true';
    if (!isEnabled) return;

    console.log('[ApiRequestStore] Setting up interception...');

    // Intercept XMLHttpRequest (catches axios requests)
    const XHR = window.XMLHttpRequest;
    this.originalXHROpen = XHR.prototype.open;
    this.originalXHRSend = XHR.prototype.send;

    XHR.prototype.open = function(method: string, url: string, ...args: any[]) {
      (this as any)._method = method;
      (this as any)._url = url;
      (this as any)._startTime = Date.now();
      (this as any)._requestId = `xhr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      return store.originalXHROpen!.apply(this, [method, url, ...args]);
    };

    XHR.prototype.send = function(body?: any) {
      const requestId = (this as any)._requestId;
      const method = (this as any)._method;
      const url = (this as any)._url;
      const startTime = (this as any)._startTime;
      const xhr = this;

      let requestPayload: any = undefined;
      if (body) {
        if (typeof body === 'string') {
          try {
            requestPayload = JSON.parse(body);
          } catch {
            requestPayload = body;
          }
        } else if (body instanceof FormData) {
          requestPayload = '<FormData>';
        } else {
          requestPayload = body;
        }
      }

      const request: ApiRequest = {
        id: requestId,
        method,
        url,
        timestamp: startTime,
        requestPayload,
        type: 'xhr',
      };

      store.addRequest(request);

      const handleLoadEnd = function() {
        const endTime = Date.now();
        const responseTime = endTime - startTime;
        const status = xhr.status;
        const statusText = xhr.statusText;

        const responseHeaders: Record<string, string> = {};
        try {
          const headerString = xhr.getAllResponseHeaders();
          if (headerString) {
            const headers = headerString.trim().split(/[\r\n]+/);
            headers.forEach((line) => {
              const parts = line.split(': ');
              const header = parts.shift();
              const value = parts.join(': ');
              if (header) {
                responseHeaders[header.toLowerCase()] = value;
              }
            });
          }
        } catch (e) {
          // Ignore
        }

        let responseData: any;
        try {
          const responseText = xhr.responseText;
          const contentType = responseHeaders['content-type'] || '';
          if (contentType.includes('application/json')) {
            try {
              responseData = JSON.parse(responseText);
            } catch {
              responseData = responseText;
            }
          } else {
            responseData = responseText;
          }
        } catch (e) {
          responseData = 'Unable to parse response';
        }

        store.updateRequest(requestId, {
          status,
          statusText,
          responseTime,
          responseHeaders,
          responseData,
        });
      };

      const handleError = function() {
        const endTime = Date.now();
        store.updateRequest(requestId, {
          status: 0,
          statusText: 'Network Error',
          responseTime: endTime - startTime,
          error: 'Request failed',
        });
      };

      xhr.addEventListener('loadend', handleLoadEnd);
      xhr.addEventListener('error', handleError);
      xhr.addEventListener('abort', handleError);

      return store.originalXHRSend!.apply(this, [body]);
    };

    // Intercept fetch
    this.originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const [url, options = {}] = args;
      const method = options.method || 'GET';
      const startTime = Date.now();
      const requestId = `fetch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const request: ApiRequest = {
        id: requestId,
        method,
        url: typeof url === 'string' ? url : url.toString(),
        timestamp: startTime,
        requestHeaders: options.headers as Record<string, string> || {},
        requestPayload: options.body ? (typeof options.body === 'string' ? JSON.parse(options.body) : options.body) : undefined,
        type: 'fetch',
      };

      store.addRequest(request);

      try {
        const response = await store.originalFetch!(...args);
        const endTime = Date.now();
        const responseTime = endTime - startTime;

        const clonedResponse = response.clone();
        let responseData: any;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            responseData = await clonedResponse.json();
          } else {
            responseData = await clonedResponse.text();
          }
        } catch (e) {
          responseData = 'Unable to parse response';
        }

        const headers: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          headers[key] = value;
        });

        store.updateRequest(requestId, {
          status: response.status,
          statusText: response.statusText,
          responseTime,
          responseHeaders: headers,
          responseData,
        });

        return response;
      } catch (error: any) {
        const endTime = Date.now();
        store.updateRequest(requestId, {
          status: 0,
          statusText: 'Network Error',
          responseTime: endTime - startTime,
          error: error.message || 'Unknown error',
        });
        throw error;
      }
    };

    this.isIntercepted = true;
    console.log('[ApiRequestStore] Interception setup complete');
  }

  teardownInterception() {
    if (!this.isIntercepted) return;

    if (this.originalFetch) {
      window.fetch = this.originalFetch;
    }
    if (this.originalXHROpen && this.originalXHRSend) {
      const XHR = window.XMLHttpRequest;
      XHR.prototype.open = this.originalXHROpen;
      XHR.prototype.send = this.originalXHRSend;
    }
    if (this.originalAxiosCreate && this.axiosModule) {
      this.axiosModule.default.create = this.originalAxiosCreate;
    }

    this.isIntercepted = false;
    console.log('[ApiRequestStore] Interception torn down');
  }
}

export const store = new ApiRequestStore();
export type { ApiRequest };

